import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Cell } from "recharts";
import { CMVDetail } from "@/hooks/useDREMonthlyData";
import { Package } from "lucide-react";

interface CMVDetailChartProps {
  data: CMVDetail[];
  total: number;
  netRevenue?: number;
}

const COLORS = [
  "hsl(0 70% 50%)",
  "hsl(25 70% 50%)",
  "hsl(45 70% 50%)",
  "hsl(15 70% 55%)",
  "hsl(35 60% 50%)",
];

function formatCurrency(value: number): string {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
  }).format(value / 100);
}

function formatShortCurrency(value: number): string {
  const absValue = Math.abs(value / 100);
  if (absValue >= 1000) {
    return `R$ ${(value / 100 / 1000).toFixed(1)}K`;
  }
  return formatCurrency(value);
}

export function CMVDetailChart({ data, total, netRevenue }: CMVDetailChartProps) {
  const cmvPercentOfRevenue = netRevenue && netRevenue > 0 ? (total / netRevenue) * 100 : 0;

  if (!data || data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Package className="h-4 w-4" />
            CMV - Custo de Mercadoria Vendida
          </CardTitle>
          <CardDescription>
            Matérias-primas, produtos de revenda e embalagens
          </CardDescription>
        </CardHeader>
        <CardContent className="h-[250px] flex items-center justify-center text-muted-foreground">
          Sem movimentações de estoque no período
        </CardContent>
      </Card>
    );
  }

  const chartConfig = data.reduce((acc, item, index) => {
    acc[item.category] = {
      label: item.category,
      color: COLORS[index % COLORS.length],
    };
    return acc;
  }, {} as Record<string, { label: string; color: string }>);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Package className="h-4 w-4" />
          CMV - Custo de Mercadoria Vendida
        </CardTitle>
        <CardDescription>
          Matérias-primas, produtos de revenda e embalagens
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="mb-4 flex items-center justify-between">
          <div>
            <p className="text-2xl font-bold">{formatCurrency(total)}</p>
            {netRevenue && netRevenue > 0 && (
              <p className="text-sm text-muted-foreground">
                {cmvPercentOfRevenue.toFixed(1)}% da receita líquida
              </p>
            )}
          </div>
        </div>
        
        <ChartContainer config={chartConfig} className="h-[200px] w-full">
          <BarChart data={data} layout="vertical" margin={{ left: 20, right: 20 }}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" horizontal={false} />
            <XAxis 
              type="number" 
              tickFormatter={formatShortCurrency}
              tick={{ fontSize: 11 }}
              tickLine={false}
              axisLine={false}
            />
            <YAxis 
              type="category" 
              dataKey="category" 
              tick={{ fontSize: 11 }}
              tickLine={false}
              axisLine={false}
              width={120}
              tickFormatter={(value) => value.length > 18 ? `${value.substring(0, 18)}...` : value}
            />
            <ChartTooltip
              content={
                <ChartTooltipContent
                  formatter={(value, name, item) => (
                    <div className="flex flex-col">
                      <span className="font-medium">{item.payload.category}</span>
                      <span>{formatCurrency(Number(value))}</span>
                      <span className="text-muted-foreground">{item.payload.percentage.toFixed(1)}% do CMV</span>
                    </div>
                  )}
                />
              }
            />
            <Bar dataKey="amount" radius={[0, 4, 4, 0]}>
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Bar>
          </BarChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
}
